import { xhr, xhrHeaders } from "../helpers/xhr";
import { User } from "../types/common.types";
import { AuthSession } from "./authTypes";
import { authStorage } from "./authStorage";

export interface LoginCredentials {
  username: string;
  password: string;
}

const normalizeUser = (rawUser: any): User => ({
  currency: rawUser.currency,
  canPlaceOrder: rawUser.canPlaceOrder,
  orderPaymentPolicy: rawUser.orderPaymentPolicy || "pay_after_session",
  paymentExperience: rawUser.paymentExperience || "hosted_checkout",
  languages: rawUser.languages,
  segments: rawUser.segments,
  legalName: rawUser.legalName,
  defaultLanguage: rawUser.defaultLanguage,
  hasSegments: rawUser.hasSegments,
  username: rawUser.username,
  activeLanguage: rawUser.defaultLanguage,
  membershipDetails: rawUser.membershipDetails,
  membershipPlan: rawUser.membershipPlan,
  contractType: rawUser.contractType,
  features: rawUser.features || [],
  address: rawUser.address,
  brief: rawUser.brief,
  contacts: rawUser.contacts,
  activities: rawUser.activities,
  businessProfile: rawUser.businessProfile,
  banner: rawUser.banner,
  logo: rawUser.logo,
  userLanguages: rawUser.userLanguages,
  isVerified: rawUser.isVerified,
  passwordResetRequired: rawUser.passwordResetRequired,
});

export const authApi = {
  async login(credentials: LoginCredentials): Promise<AuthSession> {
    const response = await xhr.post("/auth/user/login/password", credentials, {
      headers: xhrHeaders,
    });

    if (!response.data.success || !response.data.token || !response.data.user) {
      throw Object.assign(new Error(response.data.message || "Login failed"), {
        response: { status: 400, data: response.data },
      });
    }

    return {
      token: response.data.token,
      user: normalizeUser(response.data.user),
      membershipPlans: response.data.membershipPlans,
      permissions: response.data.permissions,
    };
  },

  async getSession(): Promise<AuthSession> {
    const token = authStorage.getToken();
    if (!token) throw new Error("Missing auth token");

    const response = await xhr.get("/auth/session");
    if (!response.data.success || !response.data.user) {
      throw Object.assign(new Error(response.data.message), {
        response: { status: 401, data: response.data },
      });
    }

    return {
      token,
      user: normalizeUser(response.data.user),
      membershipPlans: response.data.membershipPlans,
      permissions: response.data.permissions,
    };
  },

  requestPasswordReset(email: string) {
    return xhr.post(
      "/auth/forgot-password",
      { email },
      { headers: xhrHeaders }
    );
  },

  logout() {
    return xhr.post("/auth/user/logout");
  },

  verifyEmail(code: string) {
    return xhr.post("/user/verify-email", { code }, { headers: xhrHeaders });
  },
};
